import { useState, useRef, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';

interface AmountInputProps {
  value: string;
  onChange: (value: string) => void;
  onConfirm?: () => void; // Appelé après Enter/Tab pour signaler la fin de saisie
  onBlur?: () => void; // Appelé quand le champ perd le focus
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  // Infobulle de budget (optionnelle)
  budgetHint?: ReactNode;
}

export default function AmountInput({
  value,
  onChange,
  onConfirm,
  onBlur,
  placeholder = '0.00',
  disabled = false,
  className = '',
  budgetHint,
}: AmountInputProps) {
  const [displayValue, setDisplayValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Synchroniser displayValue avec value externe
  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  // Passer au champ suivant
  const focusNextField = useCallback(() => {
    const form = inputRef.current?.form;
    if (form) {
      const elements = Array.from(form.elements) as HTMLElement[];
      const currentIndex = elements.indexOf(inputRef.current!);
      const nextElement = elements[currentIndex + 1];
      if (nextElement && 'focus' in nextElement) {
        setTimeout(() => nextElement.focus(), 0);
      }
    }
  }, []);

  // Valider et formater le montant
  const validateAndApply = useCallback(() => {
    let cleanValue = displayValue.trim();

    // Remplacer la virgule par un point pour le parsing
    cleanValue = cleanValue.replace(',', '.');

    // Retirer les points multiples (garder seulement le premier)
    const parts = cleanValue.split('.');
    if (parts.length > 2) {
      cleanValue = parts[0] + '.' + parts.slice(1).join('');
    }

    // Valider que c'est un nombre
    if (cleanValue === '' || cleanValue === '.') {
      onChange('');
      return;
    }

    const numValue = parseFloat(cleanValue);
    if (!isNaN(numValue)) {
      // Formater avec 2 décimales si nécessaire
      onChange(cleanValue);
    } else {
      // Valeur invalide, revenir à la précédente
      setDisplayValue(value);
    }
  }, [displayValue, value, onChange]);

  // Gérer la saisie
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Autoriser seulement les chiffres, le point et la virgule
    // Regex : accepte les chiffres, un seul séparateur décimal (. ou ,)
    const sanitized = newValue.replace(/[^\d.,]/g, '');

    // Remplacer la virgule par un point pour uniformité
    let normalized = sanitized.replace(',', '.');

    // S'assurer qu'il n'y a qu'un seul point
    const dotCount = (normalized.match(/\./g) || []).length;
    if (dotCount > 1) {
      // Garder seulement le premier point
      const firstDotIndex = normalized.indexOf('.');
      normalized = normalized.substring(0, firstDotIndex + 1) +
                   normalized.substring(firstDotIndex + 1).replace(/\./g, '');
    }

    // Limiter à 2 décimales
    const parts = normalized.split('.');
    if (parts.length === 2 && parts[1].length > 2) {
      normalized = parts[0] + '.' + parts[1].substring(0, 2);
    }

    setDisplayValue(normalized);
    // Propager la valeur immédiatement pour permettre la validation en temps réel
    // L'auto-save ne sera déclenché que pour les lignes existantes (pas les nouvelles)
    onChange(normalized);
  };

  // Gérer les touches spéciales
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      validateAndApply();
      // Signaler la fin de saisie
      onConfirm?.();
      if (onConfirm) {
        focusNextField();
      }
    } else if (e.key === 'Tab') {
      // Tab gère automatiquement le focus, juste valider
      validateAndApply();
      // Signaler la fin de saisie
      onConfirm?.();
    } else if (e.key === 'Escape') {
      setDisplayValue(value);
    }
  };

  // Gérer le focus
  const handleFocus = () => {
    setIsFocused(true);
  };

  // Gérer la perte de focus
  const handleBlur = () => {
    setIsFocused(false);
    validateAndApply();
    onBlur?.();
  };

  const inputElement = (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      className={`input py-1.5 text-sm text-right ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''} ${className}`}
      value={displayValue}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
    />
  );

  // Si une infobulle de budget est fournie, wrapper dans un div relatif
  // L'infobulle ne s'affiche que quand le champ est focusé
  if (budgetHint) {
    return (
      <div className="relative">
        {isFocused && budgetHint}
        {inputElement}
      </div>
    );
  }

  return inputElement;
}
